import { useMemo, useState } from 'react';

import { Button } from '@monksflow/monks-ui';
import type { McpCatalogResponseDto } from '@inference.engine/api-client/web';

import { CardGridSkeleton } from '@/shared/ui/skeletons';
import { EmptyState } from '@/shared/ui/EmptyState';
import { McpLogo } from '@/shared/ui';

import {
  useBuiltInMcpServers,
  useConnectMcp,
  useInstallableCatalog,
  useInstalledMcps,
  useInstallMcp,
  useUninstallMcp,
  useUpdateMcpCredentials,
} from '../../model';
import { McpServerCard } from '../McpServerCard/McpServerCard';
import { McpHeadersModal } from '../McpHeadersModal/McpHeadersModal';
import { McpHealthBadge } from '../McpHealthBadge/McpHealthBadge';
import { useTranslation } from 'react-i18next';

interface ModalState {
  open: boolean;
  mode: 'install' | 'edit';
  catalogId: string;
  name: string;
  hasExistingHeaders: boolean;
}

const CLOSED_MODAL: ModalState = {
  open: false,
  mode: 'install',
  catalogId: '',
  name: '',
  hasExistingHeaders: false,
};

const GRID_CLASSES = 'grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3';

export const McpMarketplace = () => {
  const { t } = useTranslation('admin');

  const { data: catalog = [], isLoading: catalogLoading } = useInstallableCatalog();
  const { data: installed = [], isLoading: installedLoading } = useInstalledMcps();
  const installMutation = useInstallMcp();
  const connectMutation = useConnectMcp();
  const updateMutation = useUpdateMcpCredentials();
  const uninstallMutation = useUninstallMcp();

  // Built-in (platform) servers: always available, not user-managed → shown read-only, no actions.
  const { data: builtInServers = [] } = useBuiltInMcpServers();

  const [modal, setModal] = useState<ModalState>(CLOSED_MODAL);

  const installedByCatalogId = useMemo(
    () => new Map(installed.map((i) => [i.mcpCatalogId, i])),
    [installed],
  );

  const { installedEntries, availableEntries } = useMemo(() => {
    const installedList: McpCatalogResponseDto[] = [];
    const availableList: McpCatalogResponseDto[] = [];
    for (const entry of catalog) {
      (installedByCatalogId.has(entry.id) ? installedList : availableList).push(entry);
    }
    return { installedEntries: installedList, availableEntries: availableList };
  }, [catalog, installedByCatalogId]);

  const isLoading = catalogLoading || installedLoading;
  const isSubmitting = installMutation.isPending || updateMutation.isPending;

  // Only flip `open` on close — keep mode/name so the title/button stay stable while the
  // modal animates out. The open handlers below set the full state before it's shown again.
  const closeModal = () => setModal((prev) => ({ ...prev, open: false }));

  const openInstall = (entry: McpCatalogResponseDto) =>
    setModal({
      open: true,
      mode: 'install',
      catalogId: entry.id,
      name: entry.name,
      hasExistingHeaders: false,
    });

  const openEdit = (entry: McpCatalogResponseDto) =>
    setModal({
      open: true,
      mode: 'edit',
      catalogId: entry.id,
      name: entry.name,
      hasExistingHeaders: installedByCatalogId.get(entry.id)?.hasAuthHeaders ?? false,
    });

  const handleSubmit = (headers: Record<string, string>) => {
    const mutation = modal.mode === 'install' ? installMutation : updateMutation;
    mutation.mutate({ mcpCatalogId: modal.catalogId, headers }, { onSuccess: closeModal });
  };

  if (isLoading) return <CardGridSkeleton count={6} />;

  if (catalog.length === 0 && builtInServers.length === 0) {
    return (
      <EmptyState
        icon={<McpLogo size={28} />}
        title={t('userMcpServers.mcpMarketplace.noMcpServersAvailable')}
        description={t('userMcpServers.mcpMarketplace.yourOrganizationHasnTAdded')}
      />
    );
  }

  return (
    <>
      <div className="flex flex-col gap-8">
        {(builtInServers.length > 0 || installedEntries.length > 0) && (
          <section className="flex flex-col gap-4">
            <h3 className="text-body-lg font-medium">
              {t('userMcpServers.mcpMarketplace.installed')}
            </h3>
            <div className={GRID_CLASSES}>
              {builtInServers.map((server) => (
                <McpServerCard
                  key={server.id}
                  name={server.name}
                  description={server.description}
                  tag={t('userMcpServers.mcpMarketplace.builtIn')}
                  statusContent={<McpHealthBadge mcpCatalogId={server.id} />}
                />
              ))}
              {installedEntries.map((entry) => (
                <McpServerCard
                  key={entry.id}
                  name={entry.name}
                  description={entry.description}
                  statusContent={<McpHealthBadge mcpCatalogId={entry.id} />}
                  actions={
                    <>
                      {entry.authType === 'static_headers' && (
                        <Button variant="outline" onClick={() => openEdit(entry)}>
                          {t('userMcpServers.mcpMarketplace.editHeaders')}
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        onClick={() => uninstallMutation.mutate({ mcpCatalogId: entry.id })}
                        disabled={uninstallMutation.isPending}
                      >
                        {entry.authType === 'oauth'
                          ? t('userMcpServers.mcpMarketplace.disconnect')
                          : t('userMcpServers.mcpMarketplace.uninstall')}
                      </Button>
                    </>
                  }
                />
              ))}
            </div>
          </section>
        )}

        <section className="flex flex-col gap-4">
          <h3 className="text-body-lg font-medium">
            {t('userMcpServers.mcpMarketplace.available')}
          </h3>
          {availableEntries.length === 0 ? (
            <p className="text-body-md text-muted-foreground">
              {t('userMcpServers.mcpMarketplace.youVeInstalledEverythingIn')}
            </p>
          ) : (
            <div className={GRID_CLASSES}>
              {availableEntries.map((entry) => {
                const isOauth = entry.authType === 'oauth';
                const isNoAuth = entry.authType === 'no_auth';
                return (
                  <McpServerCard
                    key={entry.id}
                    name={entry.name}
                    description={entry.description}
                    statusLabel={
                      isOauth
                        ? t('userMcpServers.mcpMarketplace.oauth')
                        : isNoAuth
                          ? t('userMcpServers.mcpMarketplace.noAuthRequired')
                          : t('userMcpServers.mcpMarketplace.requiresHeaders')
                    }
                    actions={
                      isOauth ? (
                        // OAuth opens the authorize popup instead of the headers modal; the install
                        // row is created server-side when the callback completes the token exchange.
                        <Button
                          onClick={() => connectMutation.mutate({ mcpCatalogId: entry.id })}
                          disabled={connectMutation.isPending}
                        >
                          {t('userMcpServers.mcpMarketplace.connect')}
                        </Button>
                      ) : (
                        <Button
                          // no_auth needs no credentials — install in one click, skip the headers modal.
                          onClick={() =>
                            isNoAuth
                              ? installMutation.mutate({ mcpCatalogId: entry.id })
                              : openInstall(entry)
                          }
                        >
                          {t('userMcpServers.mcpMarketplace.install')}
                        </Button>
                      )
                    }
                  />
                );
              })}
            </div>
          )}
        </section>
      </div>

      <McpHeadersModal
        open={modal.open}
        mode={modal.mode}
        mcpName={modal.name}
        hasExistingHeaders={modal.hasExistingHeaders}
        isSubmitting={isSubmitting}
        onClose={closeModal}
        onSubmit={handleSubmit}
      />
    </>
  );
};
